import React, { useRef, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { Checkbox } from '../uiComponents/ComponentLibrary/Checkbox';

const customStyles: Record<string, SxProps<Theme>> = {
  setting: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '22px',
    '&:last-child': {
      marginRight: '0px',
    },
  },
  expandable: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    borderRadius: '8px',
    paddingLeft: '0.25em',
    '&:hover': {
      backgroundColor: 'rgba(32,33,36,0.039)',
    },
  },
  closeIcon: {
    transition: 'transform .1s ease-in-out',
    transform: 'rotate(180deg)',
    MsTransform: 'rotate(180deg)',
    WebkitTransform: 'rotate(180deg)',
  },
  openIcon: {
    transition: 'transform .1s ease-out',
  },
  config: {
    padding: '10px',
    borderRadius: '8px',
  },
};

interface TextValidationSettingProps {
  validateMail: '1';
  validateRutChile: boolean;
  validateContainer: boolean;
  onValidationChange: (validation: 'email' | 'rut-chile' | 'container' | null) => void;
}

const TextValidationSetting: React.FC<TextValidationSettingProps> = ({
  validateMail,
  validateRutChile,
  validateContainer,
  onValidationChange: handleValidationChange,
}) => {
  const lang = useLanguage();
  const { checkFlag } = useFeatureFlags();
  const checkContainer = checkFlag('CheckContainer');

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const handleOpenConfig = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    handleValidationChange(value as 'email' | 'rut-chile' | 'container' | null);
  };

  const open = Boolean(anchorEl);
  const selectedValidation = () => {
    if (validateMail) return 'email';
    if (validateRutChile) return 'rut-chile';
    if (validateContainer) return 'container';
    return null;
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setAnchorEl(anchorRef.current);
      handleValidationChange('email');
    } else {
      handleValidationChange(null);
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Checkbox
        size='small'
        checked={Boolean(validateMail) || Boolean(validateRutChile) || Boolean(validateContainer)}
        onChange={handleCheckboxChange}
        onClick={(event) => event.stopPropagation()}
      />
      <Box
        sx={customStyles.expandable}
        onClick={handleOpenConfig}
        ref={anchorRef}
      >
        <Typography variant='body1'>{lang.form_builder.validate}</Typography>
        <ExpandMoreIcon sx={anchorEl ? customStyles.closeIcon : customStyles.openIcon} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        elevation={5}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={customStyles.config}>
          <RadioGroup
            value={selectedValidation()}
            onChange={handleOptionChange}
          >
            <FormControlLabel
              value='email'
              control={
                <Radio
                  size='small'
                  color='primary'
                />
              }
              label={lang.form_builder.validateMail}
            />
            <FormControlLabel
              value='rut-chile'
              control={
                <Radio
                  size='small'
                  color='primary'
                />
              }
              label={lang.form_builder.validateRutChile}
            />
            {checkContainer && (
              <FormControlLabel
                value='container'
                control={
                  <Radio
                    size='small'
                    color='primary'
                  />
                }
                label={lang.form_builder.validateContainer}
              />
            )}
          </RadioGroup>
        </Box>
      </Popover>
    </Box>
  );
};

export default TextValidationSetting;
